import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import instance from "@store/middleware/api";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import { BottomFade, ScrollArea, ScrollChevron, ScrollHint, useScrollAffordance } from "@/components/CartModal/phoneTemplate";
import { useAuth } from "@/hooks/auth";
import { useModalAnimation } from "@/hooks/useModalAnimation";
import { colors, fonts, fontSizes, radius, transitions } from "@/styles/design-system";
import { getStripe } from "@/utils/stripe";
import { writeSubscriptionAlert } from "@/utils/subscriptionAlert";


/* ── Animations ── */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(24px) scale(0.97); }
`;
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/* ── Styled Components ── */

const Overlay = styled.div`
  position: fixed;
  /* Size to the VISIBLE viewport (100dvh), not inset:0 — a fixed overlay spans
     the layout viewport which extends behind the iOS Safari bottom bar, so a
     centred modal can sit partly behind it. (Per docu/modal-template-phone.md.) */
  top: 0;
  left: 0;
  right: 0;
  height: 100dvh;
  box-sizing: border-box;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  /* Small side gap so the modal never touches the phone's screen edges. */
  padding: 0 14px;
`;

const ModalCard = styled.div<{ $closing?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 460px;
  /* dvh, never vh — tracks the visible viewport as the iOS bar shows/hides, so
     the body's flex:1 overflow stays correctly bounded (scroll affordance works). */
  max-height: 90dvh;
  /* Required so the absolutely-positioned ✕ anchors to the card, not the overlay. */
  position: relative;
  /* Static glass snapshot (2026-05-13) — see CartModal/styles.ts for context. */
  background:
    radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
    radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
    radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
    linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%);
  border: none;
  border-radius: 24px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6);
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;
  overflow: hidden;
`;

const ModalHead = styled.div<{ $centered?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  /* Vertical: 16px above + 36px ✕ + 16px below = 68px → ✕ has equal gap above/below.
     Centred (success): symmetric 52px so the title is dead-centre on the true width.
     Left (update form): 24px left inset, 56px right to clear the ✕. */
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  min-height: 68px;
  padding: ${({ $centered }) => ($centered ? "0 52px" : "0 56px 0 24px")};
`;

const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Don't chain the scroll into the page behind the modal at the edges. */
  overscroll-behavior: contain;
  padding: 24px 24px 18px;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar { width: 12px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.5);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.7);
    background-clip: padding-box;
  }
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  padding: 12px 24px 18px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(235, 194, 142, 0.3);
  background: rgba(235, 194, 142, 0.2);
  color: rgba(255, 255, 255, 0.55);
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background ${transitions.fast}, color ${transitions.fast};
  @media (hover: hover) {
    &:hover { background: rgba(235, 194, 142, 0.35); color: #fff; }
  }
  &:active { background: rgba(235, 194, 142, 0.35); color: #fff; }
`;

const Title = styled.h2<{ $centered?: boolean }>`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.5rem;
  font-weight: 400;
  color: #fff;
  margin: 0;
  line-height: 1.2;
  text-align: ${({ $centered }) => ($centered ? "center" : "left")};
`;

const Subtitle = styled.p`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: #fff;
  margin: 0;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 14px 28px;
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 600;
  cursor: pointer;
  transition: all ${transitions.normal};
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  border: none;
  color: ${colors.brown.darkest};
  box-shadow: 0 0 24px rgba(186, 144, 92, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  @media (hover: hover) {
    &:hover:not(:disabled) { box-shadow: 0 4px 32px rgba(186, 144, 92, 0.45); }
  }
  &:disabled {
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.45);
    box-shadow: none;
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

/* ── Inner Form ── */

interface UpdateFormProps {
  onSuccess: () => void;
  onError: (msg: string) => void;
  isPastDue?: boolean;
  failedAmount?: number;
  failedDate?: string;
}

const UpdateForm: React.FC<UpdateFormProps> = ({ onSuccess, onError, isPastDue, failedAmount, failedDate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // The card form can overflow on small phones — drive the mandatory scroll
  // affordance (bottom fade + "Scroll ⌄"). UpdateForm only renders once its body
  // is in the DOM, so no [mounted] dep is needed here.
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (result.error) {
        setFormError(result.error.message || "Failed to update payment method.");
        onError(result.error.message || "Failed to update payment method.");
        setSubmitting(false);
        return;
      }

      // Get the payment method ID from the confirmed SetupIntent
      const setupIntent = result.setupIntent;
      if (setupIntent?.payment_method) {
        const pmId = typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;

        await instance.post("/stripe/set-default-payment-method", { paymentMethodId: pmId });
      }

      onSuccess();
    } catch (err: any) {
      onError(err?.message || "Something went wrong.");
      setSubmitting(false);
    }
  }, [stripe, elements, submitting, onSuccess, onError]);

  return (
    <>
      <ScrollArea>
        <ModalBody ref={bodyRef}>
          {/* Description / past-due notice — lives in the scroll body so the fixed
              header stays a clean symmetric-✕ title bar (per the modal template). */}
          {isPastDue && failedAmount ? (
            <Subtitle style={{ marginBottom: 20 }}>
              Your payment of <span style={{ fontFamily: fonts.number, fontWeight: 600, color: "#fff" }}>${failedAmount}</span>
              {failedDate && <> on <span style={{ fontFamily: fonts.number, fontWeight: 600, color: "#fff" }}>{new Date(failedDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span></>} could not be processed.
            </Subtitle>
          ) : !isPastDue ? (
            <Subtitle style={{ marginBottom: 20 }}>Enter a new card to replace your current payment method.</Subtitle>
          ) : null}
          {formError && (
            <div style={{
              padding: "10px 14px", borderRadius: "8px",
              background: "rgba(228, 122, 106, 0.12)", border: "1px solid rgba(228, 122, 106, 0.3)",
              color: "#e47a6a", fontSize: "0.875rem", fontFamily: "'Raleway', sans-serif",
              marginBottom: "16px",
            }}>{formError}</div>
          )}
          <div>
            <PaymentElement
              onReady={() => setReady(true)}
              options={{
                layout: "tabs",
                business: { name: "Audio Sanctum" },
                // `link: "never"` disables Link's auto-detect express-checkout
                // takeover. Without this, a Link-recognised email hijacks the
                // whole element and renders the saved-Link card directly,
                // hiding the Card/Link tabs entirely — users updating their
                // PM can't see a path to add a different card. Link is still
                // available as a regular tab via the SetupIntent's
                // `payment_method_types: ["card", "link"]`.
                wallets: { applePay: "auto", googlePay: "auto", link: "never" },
              }}
            />
          </div>
        </ModalBody>
        <BottomFade $visible={canScrollDown}><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <ModalFooter>
        <PrimaryBtn onClick={handleSubmit} disabled={submitting || !ready}>
          {submitting && <Spinner aria-hidden />}
          {(() => {
            // No glyph while submitting — the spinner already sits in that slot
            // and two icons on one button reads as clutter.
            if (submitting) return isPastDue ? "Processing payment…" : "Updating…";
            if (!isPastDue) return <ButtonLabel glyph="card" muted={!ready}>Update Payment Method</ButtonLabel>;
            const amt = typeof failedAmount === "number" ? `$${failedAmount.toFixed(2)}` : null;
            return <ButtonLabel glyph="card" muted={!ready}>{amt ? `Update & Pay ${amt}` : "Update & Pay"}</ButtonLabel>;
          })()}
        </PrimaryBtn>
      </ModalFooter>
    </>
  );
};

/* ── Modal ── */

interface UpdatePaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  isPastDue?: boolean;
  failedAmount?: number;
  failedDate?: string;
}

const UpdatePaymentMethodModal: React.FC<UpdatePaymentMethodModalProps> = ({
  open, onClose, isPastDue, failedAmount, failedDate,
}) => {
  const { mounted, closing } = useModalAnimation(open);
  const { myData } = useAuth();
  const userId: string | null = (myData as any)?.id ?? (myData as any)?._id ?? null;
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /** PAST_DUE → ACTIVE recovery banner. After the user's PM update fires
      the backend retry, the `invoice.paid` webhook flips the sub back to
      ACTIVE and re-anchors the cycle. We surface that as a green
      persistent alert on /account/subscription + /account/claims so the
      user knows their service is back online and what their new cycle
      end date is. Pattern mirrors Reactivate / Keep Plan. */
  const writeRecoveryAlert = useCallback(async () => {
    if (!isPastDue || !userId) return;
    // Poll /subscriptions/my for up to ~5s waiting for the invoice.paid
    // webhook + recovery branch to flip status ACTIVE. Without polling,
    // a fast user click on Done can race the webhook and short-circuit
    // before the new period dates are available. Always write SOMETHING
    // so the banner shows even if polling never hits ACTIVE.
    const deadline = Date.now() + 5000;
    let finalData: any = null;
    while (Date.now() < deadline) {
      try {
        const { data } = await instance.get("/subscriptions/my");
        if (data?.status === "ACTIVE" && data?.currentPeriodEnd) {
          finalData = data;
          break;
        }
      } catch { /* try again until deadline */ }
      await new Promise((r) => setTimeout(r, 400));
    }
    if (finalData) {
      const periodWord = finalData.billingPeriod === "annual" ? "annual" : "monthly";
      writeSubscriptionAlert(
        userId,
        `Payment was processed successfully and your subscription is active again. Your new ${periodWord} billing cycle was set to run from today.`,
      );
    } else {
      // Polling timed out — recovery webhook hasn't landed yet. Still
      // surface a banner so the user sees feedback; cycle dates will
      // appear correctly on their next page load.
      writeSubscriptionAlert(userId, "Payment was processed successfully and your subscription is active again.");
    }
  }, [isPastDue, userId]);

  // Create SetupIntent when modal opens
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setError(null);
      setSuccess(false);
      return;
    }
    instance.post("/stripe/setup-intent").then((res) => {
      setClientSecret(res.data.clientSecret);
    }).catch(() => {
      setError("Could not initialize payment form. Please try again.");
    });
  }, [open]);

  const handleClose = useCallback(() => { onClose(); }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  if (!mounted) return null;

  return createPortal(
    <Overlay>
      <ModalCard $closing={closing} onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={handleClose}>&#x2715;</CloseBtn>
        <ModalHead $centered={success}>
          <Title $centered={success}>{success ? "Success" : "Update Payment Method"}</Title>
        </ModalHead>

        {success ? (
          <>
            <ModalBody>
              <div style={{ textAlign: "center", padding: "0 0 20px" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "rgba(52, 211, 153, 0.1)", border: "1px solid rgba(52, 211, 153, 0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                }}>
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: "#fff", fontWeight: 500, marginBottom: 8 }}>
                  Your payment method has been updated.
                </div>
                <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(251, 244, 236, 0.6)" }}>
                  {isPastDue ? "Your failed payment will be retried automatically." : "Your next billing will use the new payment method."}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <PrimaryBtn onClick={async () => {
                await writeRecoveryAlert();
                handleClose();
                window.location.reload();
              }}>
                Done
              </PrimaryBtn>
            </ModalFooter>
          </>
        ) : error && !clientSecret ? (
          <ModalBody>
            <div style={{
              padding: "14px 18px", borderRadius: radius.xl,
              background: "rgba(228, 122, 106, 0.12)", border: "1px solid rgba(228, 122, 106, 0.3)",
              fontFamily: fonts.body, fontSize: fontSizes.body, color: colors.accent.red,
              marginBottom: 20,
            }}>{error}</div>
          </ModalBody>
        ) : clientSecret ? (
          <Elements
            stripe={getStripe()}
            options={{
              clientSecret,
              appearance: {
                theme: "flat",
                variables: {
                  colorPrimary: "#ebc28e",
                  colorBackground: "#513a28",
                  colorText: "#ffffff",
                  colorTextSecondary: "rgba(251, 244, 236, 0.8)",
                  colorTextPlaceholder: "rgba(255, 255, 255, 0.7)",
                  colorDanger: "#e47a6a",
                  colorIcon: "#ebc28e",
                  colorIconHover: "#f7ede6",
                  fontFamily: "'Raleway', sans-serif",
                  fontSizeBase: "0.9375rem",
                  borderRadius: "8px",
                  spacingUnit: "4px",
                  focusBoxShadow: "none",
                  focusOutline: "none",
                },
                rules: {
                  ".Input": {
                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "#ffffff",
                    // 16px so iOS Safari doesn't auto-zoom the card fields on focus.
                    fontSize: "16px",
                    padding: "14px 16px",
                    boxShadow: "none",
                    transition: "border-color 0.2s ease",
                  },
                  ".Input:focus": {
                    border: "1px solid rgba(235, 194, 142, 0.5)",
                    boxShadow: "none",
                    outline: "none",
                  },
                  ".Input::placeholder": { color: "rgba(255, 255, 255, 0.8)" },
                  ".Input--invalid": { border: "1px solid #e47a6a", boxShadow: "none" },
                  ".Label": { color: "rgba(251, 244, 236, 0.7)", fontSize: "0.8125rem", fontWeight: "500", marginBottom: "6px" },
                  ".Tab": { backgroundColor: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.15)", color: "rgba(251, 244, 236, 0.6)", boxShadow: "none" },
                  ".Tab:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)", border: "1px solid rgba(235, 194, 142, 0.3)", color: "#fbf4ec" },
                  ".Tab--selected": { backgroundColor: "rgba(235, 194, 142, 0.1)", border: "1px solid rgba(235, 194, 142, 0.4)", color: "#ebc28e", boxShadow: "none" },
                  ".Tab--selected:hover": { backgroundColor: "rgba(235, 194, 142, 0.12)", color: "#ebc28e" },
                  ".TabIcon": { fill: "rgba(251, 244, 236, 0.5)" },
                  ".TabIcon--selected": { fill: "#ebc28e" },
                  ".TabLabel": { color: "inherit" },
                  ".Block": { backgroundColor: "transparent", border: "none", boxShadow: "none" },
                  ".DropdownItem": { backgroundColor: "rgba(81, 58, 40, 0.95)", color: "#fbf4ec" },
                  ".DropdownItem--highlight": { backgroundColor: "rgba(235, 194, 142, 0.15)" },
                },
              },
            }}
          >
            <UpdateForm
              isPastDue={isPastDue}
              failedAmount={failedAmount}
              failedDate={failedDate}
              onSuccess={() => setSuccess(true)}
              onError={(msg) => setError(msg)}
            />
          </Elements>
        ) : (
          <ModalBody>
            <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(251, 244, 236, 0.5)", fontFamily: fonts.body, fontSize: fontSizes.body }}>
              Loading...
            </div>
          </ModalBody>
        )}
      </ModalCard>
    </Overlay>,
    document.body,
  );
};

export default UpdatePaymentMethodModal;
